import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Clipboard, CircleCheck } from "lucide-react";
import { normalizeUrl, getHostname } from "../../utils/urlUtils";
import Button from "../common/Button";
import IconButton from "../common/IconButton";

function UrlBar({ url, setUrl, onPreview }) {
  const [touched, setTouched] = useState(false);
  const [justLoaded, setJustLoaded] = useState(null);
  const successTimerRef = useRef(null);
  const validation = useMemo(() => normalizeUrl(url), [url]);
  const showError = touched && url.trim().length > 0 && !validation.valid;

  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (!validation.valid) return;

    onPreview(validation.url);
    setJustLoaded(getHostname(validation.url));
    clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setJustLoaded(null), 4000);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text);
    } catch {
      // Clipboard permission denied or unavailable — silently ignore,
      // the user can still paste manually into the field.
    }
  };

  return (
    <form className="url-bar" onSubmit={handleSubmit} role="search">
      <label htmlFor="url-input" className="sr-only">
        Website URL
      </label>
      <div className={`url-input-group ${showError ? "url-input-error" : ""}`}>
        <input
          id="url-input"
          type="text"
          inputMode="url"
          placeholder="Enter URL (example.com, localhost:3000, 192.168.1.10:8080)"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            setJustLoaded(null);
          }}
          onBlur={() => setTouched(true)}
          aria-invalid={showError}
          aria-describedby={showError ? "url-error" : "url-hint"}
        />
        {url && (
          <IconButton label="Clear URL" onClick={() => { setUrl(""); setTouched(false); setJustLoaded(null); }}>
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
        )}
        <IconButton label="Paste from clipboard" tooltip="Paste" onClick={handlePaste}>
          <Clipboard size={16} strokeWidth={1.75} aria-hidden="true" />
        </IconButton>
      </div>

      <Button type="submit" variant="primary" disabled={!validation.valid}>
        Preview
      </Button>

      <div className="field-message-slot" aria-live="polite">
        {showError && (
          <p id="url-error" className="field-message field-message-error" role="alert">
            {validation.error}
          </p>
        )}
        {!showError && justLoaded && (
          <p className="field-message field-message-success">
            <CircleCheck size={14} strokeWidth={1.75} aria-hidden="true" /> Loaded {justLoaded}
          </p>
        )}
        {!showError && !justLoaded && validation.valid && url.trim() !== validation.url && (
          <p id="url-hint" className="field-message">
            Will load: {validation.url}
          </p>
        )}
      </div>
    </form>
  );
}

export default UrlBar;
